import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { IconButton, Typography } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import NorthIcon from "@mui/icons-material/North";
import WestIcon from "@mui/icons-material/West";
import SouthIcon from "@mui/icons-material/South";
import { useDispatch } from "react-redux";
import {
  updateIsUserAdding,
  updateWallsManagment,
} from "../../../reducers/configurator/configuratorSlice";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PopoverPicker from "../../qrCodeManagement/PopoverColorPicker";
import { hexToRgba, rgbaToHex } from "../../../helpers";

const PREFIX = "wallsDetails";

const classes = {
  root: `${PREFIX}-root`,
  optionsContainer: `${PREFIX}-optionsContainer`,
  orientationOptions: `${PREFIX}-orientationOptions`,
  arrowsContainer: `${PREFIX}-arrowsContainer`,
  orientationNorth: `${PREFIX}-orientationNorth`,
  orientationEast: `${PREFIX}-orientationEast`,
  orientationSouth: `${PREFIX}-orientationSouth`,
  orientationWest: `${PREFIX}-orientationWest`,
  wallSelect: `${PREFIX}-wallSelect`,
  marginBottom: `${PREFIX}-marginBottom`,
  textPosition: `${PREFIX}-textPosition`,
  selectMargin: `${PREFIX}-selectMargin`,
  textStyle: `${PREFIX}-textStyle`,
};

const WallsDetailsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      width: "100%",
    },
    [`& .${classes.optionsContainer}`]: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      height: "100%",
      margin: "1.5rem 0",
      [theme.breakpoints.between("xs", "sm")]: {
        margin: "1.5rem 0 0 0",
      },
    },
    [`& .${classes.wallSelect}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "130px",
      height: "144px",
      marginRight: "2rem",
      [theme.breakpoints.between("xs", "sm")]: {
        marginBottom: "57px",
      },
    },
    [`& .${classes.orientationOptions}`]: {
      width: "20%",
      height: "65%",
      [theme.breakpoints.between("xs", "sm")]: {
        width: "32%",
        height: "67%",
      },
    },
    [`& .${classes.arrowsContainer}`]: {
      position: "relative",
      height: "calc(120px - 1rem)",
    },
    [`& .${classes.orientationNorth}`]: {
      position: "absolute",
      top: "0",
      left: "50%",
      transform: "translate(-50%, 0)",
    },
    [`& .${classes.orientationEast}`]: {
      position: "absolute",
      right: "0",
      top: "50%",
      transform: "translate(0, -50%)",
    },
    [`& .${classes.orientationSouth}`]: {
      position: "absolute",
      bottom: "0",
      left: "50%",
      transform: "translate(-50%, 0)",
    },
    [`& .${classes.orientationWest}`]: {
      position: "absolute",
      left: "0",
      top: "50%",
      transform: "translate(0, -50%)",
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem",
      textAlign: "center",
    },
    [`& .${classes.selectMargin}`]: {
      marginBottom: "25px",
    },
    [`& .${classes.textStyle}`]: {
      marginTop: "0.5rem",
      textAlign: "center",
    },
  };
});

const StyledIconButton = styled(IconButton)(({ theme, variant, color }) => {
  const overrides = {};

  const colorAsVariant =
    color === undefined || color === "inherit" || color === "default"
      ? "primary"
      : color;

  if (variant === "contained") {
    overrides.backgroundColor = theme.palette[colorAsVariant].main;
    overrides.color = theme.palette[colorAsVariant].contrastText;
    overrides[":hover"] = {
      backgroundColor: theme.palette[colorAsVariant].dark,
    };
  }

  if (variant === "outlined") {
    overrides.border = `1px solid ${theme.palette[colorAsVariant].main}`;
    overrides.color = theme.palette[colorAsVariant].main;
  }

  return {
    ...overrides,
  };
});

const WallsDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [hexaInput, setHexaInput] = useState("#000000");
  const [inputValue, setInputValue] = useState({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  });

  const wallOptions = useSelector(
    (state) => state.configurator?.wallsManagement
  );
  const isUserAdding = useSelector((state) => state.configurator?.isUserAdding);
  const isUserDeleting = useSelector(
    (state) => state.configurator?.isUserDeleting
  );

  const handleInputChange = (event) => {
    setInputValue(event);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(
        updateWallsManagment({
          ...wallOptions,
          color: inputValue,
        })
      );
      setHexaInput(
        rgbaToHex(
          `rgba(${inputValue.r}, ${inputValue.g}, ${inputValue.b}, ${inputValue.a})`
        )
      );
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleHexChange = (hex) => {
    setHexaInput(hex);
  };

  useEffect(() => {
    setInputValue(hexToRgba(hexaInput));
  }, [hexaInput]);

  return (
    <WallsDetailsContainer sx={{ width: "100%", height: "100%" }}>
      <div>
        {wallOptions.mode === "add" ? (
          <Typography variant="body1" className={classes.textStyle}>
            {t("configurator.wallDescriptionAdd")}
          </Typography>
        ) : (
          <Typography variant="body1" className={classes.textStyle}>
            {t("configurator.wallDescriptionDelete")}
          </Typography>
        )}
      </div>
      <div className={classes.optionsContainer}>
        <div className={classes.wallSelect}>
          <Typography variant="body1" className={classes.marginBottom}>
            {t("configurator.wallSelector")}
          </Typography>
          <PopoverPicker
            width={50}
            color={wallOptions.color}
            hexColor={hexaInput}
            onChangeHex={handleHexChange}
            onChange={(e) => {
              handleInputChange(e);
            }}
            mode="rgba"
          />
        </div>
        <div className={classes.orientationOptions}>
          <Typography variant="body1" className={classes.marginBottom}>
            {t("configurator.orientationSelector")}
          </Typography>
          <div className={classes.arrowsContainer}>
            <StyledIconButton
              className={classes.orientationNorth}
              variant={
                wallOptions.orientation === "north" ? "contained" : "outlined"
              }
              onClick={() => {
                dispatch(
                  updateWallsManagment({
                    ...wallOptions,
                    orientation: "north",
                  })
                );
                if (isUserDeleting) dispatch(updateIsUserAdding(false));
                else dispatch(updateIsUserAdding(true));
              }}
            >
              <NorthIcon />
            </StyledIconButton>
            <StyledIconButton
              className={classes.orientationEast}
              variant={
                wallOptions.orientation === "east" ? "contained" : "outlined"
              }
              onClick={() => {
                dispatch(
                  updateWallsManagment({
                    ...wallOptions,
                    orientation: "east",
                  })
                );
                if (isUserDeleting) dispatch(updateIsUserAdding(false));
                else dispatch(updateIsUserAdding(true));
              }}
            >
              <EastIcon />
            </StyledIconButton>
            <StyledIconButton
              className={classes.orientationSouth}
              variant={
                wallOptions.orientation === "south" ? "contained" : "outlined"
              }
              onClick={() => {
                dispatch(
                  updateWallsManagment({
                    ...wallOptions,
                    orientation: "south",
                  })
                );
                if (isUserDeleting) dispatch(updateIsUserAdding(false));
                else dispatch(updateIsUserAdding(true));
              }}
            >
              <SouthIcon />
            </StyledIconButton>
            <StyledIconButton
              className={classes.orientationWest}
              variant={
                wallOptions.orientation === "west" ? "contained" : "outlined"
              }
              onClick={() => {
                dispatch(
                  updateWallsManagment({
                    ...wallOptions,
                    orientation: "west",
                  })
                );
                if (isUserDeleting) dispatch(updateIsUserAdding(false));
                else dispatch(updateIsUserAdding(true));
              }}
            >
              <WestIcon />
            </StyledIconButton>
          </div>
        </div>
      </div>
    </WallsDetailsContainer>
  );
};

export default WallsDetails;
